<script setup lang="ts">
import FacadeArrowImage from "@/views/Calculators/AzimuthFacade/components/FacadeArrowImage.vue";
import { computed, onMounted, ref, watch } from "vue";
import SanHeCircularImage from "@/views/Calculators/AzimuthFacade/components/SanHeCircularImage.vue";
import MountainsCircleImage from "@/views/Calculators/AzimuthFacade/components/MountainsCircleImage.vue";
import AnimalsCircleImage from "@/views/Calculators/AzimuthFacade/components/AnimalsCircleImage.vue";
import { useMap } from "@/utils/useMap";
import { useRotation } from "@/utils/useRotation";
import searchIcon from "@/assets/icons/search.svg";
import CopyIcon from "@/components/icons/CopyIcon.vue";
import { useI18n } from "vue-i18n";
import { PlacePrediction } from "@/types/calculators";
import { CustomControl, GoogleMap } from "vue3-google-map";
import { useGoogleAutocomplete } from "@/utils/useGoogleAutocomplete";
import FlyingStarsControls from "@/views/Calculators/AzimuthFacade/components/FlyingStarsControls.vue";
import { useFlyingStars } from "@/utils/useFlyingStars";
import FlyingStarsComponent from "@/views/Calculators/AzimuthFacade/components/FlyingStarsComponent.vue";
import { parseCoordinates } from "@/utils/coordinates";

const googleMapKey = process.env.VUE_APP_GOOGLE_MAP_KEY;
let timeout: number | null = null;

const arrowRef = ref<HTMLImageElement | null>(null);
const magneticCircleRef = ref<HTMLElement | null>(null);
const query = ref("");
const coordinateQuery = ref("");
const isCoordinatesValid = ref<boolean | null>(null);
const myMapRef = ref();
const selectedCircle = ref<"sanhe" | "mountains" | "animals">("mountains");
const selectedColor = ref("#FF0000");
const showStars = ref<boolean>(false);
const mapCenter = ref({ lat: 50.45, lng: 30.52 });

const circleComponent = computed(
  () =>
    ({
      sanhe: SanHeCircularImage,
      mountains: MountainsCircleImage,
      animals: AnimalsCircleImage,
    }[selectedCircle.value])
);
const displayAngle = computed(() => {
  const angle = rotateAngle.value - circleAngle.value;
  if (angle < 0) {
    return (angle + 360).toFixed(2);
  }
  return angle.toFixed(2);
});

const { circleAngle, endMoveMap } = useMap(magneticCircleRef);
const { rotateAngle, startRotation, isDragging } = useRotation(arrowRef);
const { options, fetchSuggestions, getGeocoder } = useGoogleAutocomplete(
  "ru",
  "address"
);
const { t } = useI18n();
const {
  fillYearStar,
  fillMonthStar,
  fillBuildYearStars,
  buildMountStars,
  azimuthFacade,
  flyingStars,
} = useFlyingStars();

onMounted(() => {
  getCurrentUserPosition();
});

function getCurrentUserPosition() {
  if (!navigator.geolocation) {
    console.warn("Geolocation is not supported.");
    return;
  }
  navigator.geolocation.getCurrentPosition(async (position) => {
    const { latitude, longitude } = position.coords;
    mapCenter.value = { lat: latitude, lng: longitude };
  });
}

watch(
  () => isDragging.value,
  (oldValue, newValue) => {
    if (!oldValue && newValue) {
      azimuthFacade.value = Number(displayAngle.value);
      buildMountStars();
    }
  }
);

function onInput() {
  if (timeout) clearTimeout(timeout);
  timeout = window.setTimeout(() => fetchSuggestions(query.value), 700);
}

async function centerMapBeCoordinates() {
  const coordinates = parseCoordinates(coordinateQuery.value);
  if (coordinates === null) {
    isCoordinatesValid.value = false;
  } else {
    isCoordinatesValid.value = true;
    myMapRef.value.map.setCenter(coordinates);
  }
}

async function selectOption(city: PlacePrediction) {
  query.value = city.label;
  try {
    const geocoder = await getGeocoder();
    if (geocoder) {
      await getGeocoderLocation(geocoder, city.label);
    } else {
      await getApproximatelyLocation(city);
    }
  } catch (e) {
    console.error("Error getting prediction place: ", e);
  }
}

async function getGeocoderLocation(
  geocoder: google.maps.Geocoder,
  label: string
) {
  const result = await geocoder.geocode({ address: label });
  if (!result || result.results.length < 0) {
    throw new Error("Geocode api didn't send a response");
  }

  const location = result.results[0].geometry.location;
  myMapRef.value.map.setCenter(location);
}

async function getApproximatelyLocation(city: PlacePrediction) {
  const place = city.place;
  await place.fetchFields({ fields: ["location"] });

  myMapRef.value.map.setCenter(place.location);
}

async function copyAngle() {
  try {
    await navigator.clipboard.writeText(displayAngle.value.toString());
  } catch (err) {
    console.error("Ошибка копирования:", err);
  }
}

function onIdle() {
  if (myMapRef.value !== undefined) {
    const center = myMapRef.value.map.getCenter();
    if (center && center.lat() && center.lng()) {
      endMoveMap(center.lat(), center.lng());
    }
  }
}

function handleBuildYearChange(year: number) {
  fillBuildYearStars(year);
}

function handleMonthStarsChange(year: number, month: number) {
  fillMonthStar(year, month);
}

function handleYearStarsChange(year: number, month: number) {
  fillYearStar(year);
  fillMonthStar(year, month);
}

function handleToggleShowStars(
  show: boolean,
  buildYear: number,
  yearStars: number,
  monthStars: number
) {
  if (show) {
    fillBuildYearStars(buildYear);
    fillYearStar(yearStars);
    fillMonthStar(yearStars, monthStars);
  }
  showStars.value = show;
}
</script>

<template>
  <!-- MAIN CONTAINER -->
  <div
    class="w-full h-full px-4 xl:px-10 pt-6 xl:pt-[89px] pb-[20px] flex flex-col gap-4"
  >
    <div
      class="flex max-md:flex-col gap-4 md:gap-x-10 md:gap-y-3 flex-wrap md:items-center"
    >
      <!-- SEARCH CITY -->
      <div class="relative max-sm:w-full w-[233px] h-[52px] group">
        <input
          type="text"
          v-model="query"
          @input="onInput"
          :placeholder="t('calculators.azm.search-placeholder')"
          class="w-full h-full input-shadow rounded-lg pl-[46px] pr-4 text-base leading-[20px] text-muted bg-no-repeat bg-[length:16px_16px] bg-[center_left_16px]"
          :style="{ backgroundImage: `url(${searchIcon})` }"
        />

        <!-- Dropdown -->
        <ul
          v-if="options.length"
          class="absolute left-0 right-0 bg-white border rounded mt-1 max-h-60 overflow-y-auto z-10 hidden group-focus-within:block"
        >
          <li
            v-for="option in options"
            :key="option.label"
            @mousedown="selectOption(option)"
            class="px-3 py-2 hover:bg-gray-100 cursor-pointer"
          >
            {{ option.label }}
          </li>
        </ul>
      </div>

      <input
        type="text"
        v-model="coordinateQuery"
        @input="centerMapBeCoordinates"
        :placeholder="t('calculators.azm.coordinate-search-placeholder')"
        class="relative max-sm:w-full w-56 h-[52px] input-shadow rounded-lg px-4 text-base leading-[20px] text-muted outline-none"
        :class="{
          'border border-success': isCoordinatesValid,
          'border border-error': isCoordinatesValid === false,
        }"
      />

      <!-- FACADE'S ANGLE AND MAGNETIC NORTH -->
      <div class="flex items-center gap-4">
        <div class="flex items-center gap-2">
          <div class="text-base leading-[20px] min-w-44 text-left">
            {{
              t("calculators.azm.facade-angle", {
                angle: displayAngle,
              })
            }}
          </div>
          <button
            class="size-9 border border-brown rounded-lg"
            @click="copyAngle"
          >
            <CopyIcon class="text-black mx-auto" />
          </button>
        </div>

        <div class="text-base leading-[20px]">
          {{ t("calculators.azm.magnetic-north", { angle: circleAngle }) }}
        </div>
      </div>

      <!-- CIRCLE SELECTOR -->
      <div class="flex gap-x-2" role="group">
        <input
          type="radio"
          name="circleType"
          id="option1"
          value="sanhe"
          v-model="selectedCircle"
          class="sr-only peer"
        />
        <label
          for="option1"
          class="p-2 rounded-lg cursor-pointer transition-colors border border-brown text-base leading-[20px]"
          :class="
            selectedCircle === 'sanhe'
              ? 'text-white bg-brown'
              : 'text-brown bg-transparent'
          "
        >
          SanHe
        </label>

        <input
          type="radio"
          name="circleType"
          id="option3"
          value="animals"
          v-model="selectedCircle"
          class="sr-only peer"
        />
        <label
          for="option3"
          class="p-2 rounded-lg cursor-pointer transition-colors border border-brown text-base leading-[20px]"
          :class="
            selectedCircle === 'animals'
              ? 'text-white bg-brown'
              : 'text-brown bg-transparent'
          "
        >
          Animals
        </label>

        <input
          type="radio"
          name="circleType"
          id="option2"
          value="mountains"
          v-model="selectedCircle"
          class="sr-only peer"
        />
        <label
          for="option2"
          class="p-2 rounded-lg cursor-pointer transition-colors border border-brown text-base leading-[20px]"
          :class="
            selectedCircle === 'mountains'
              ? 'text-white bg-brown'
              : 'text-brown bg-transparent'
          "
        >
          Mountains
        </label>
      </div>

      <!-- COLOR CHOICE -->
      <label class="relative block w-9 h-9 rounded-lg border cursor-pointer">
        <input
          type="color"
          v-model="selectedColor"
          class="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
        />
        <div class="w-full h-full rounded-lg border border-muted p-2">
          <div
            class="w-full h-full rounded"
            :style="{ backgroundColor: selectedColor }"
          ></div>
        </div>
      </label>
    </div>

    <!-- Карта -->
    <div class="relative w-full h-full rounded-[20px] z-0 overflow-hidden">
      <GoogleMap
        :api-key="googleMapKey"
        ref="myMapRef"
        :center="mapCenter"
        :zoom="13"
        class="w-full h-full"
        @idle="onIdle"
        :libraries="['places']"
        :disable-default-ui="true"
        :zoom-control="true"
        :map-type-control="true"
        :map-type-control-options="{
          position: 20.0,
        }"
      />

      <FlyingStarsControls
        @update:buildYear="handleBuildYearChange"
        @update:monthStars="handleMonthStarsChange"
        @update:yearStars="handleYearStarsChange"
        @toggle:showStars="handleToggleShowStars"
      />
      <!-- Магнитный круг -->
      <div
        ref="magneticCircleRef"
        class="absolute top-1/2 left-1/2 z-[1000] translate-x-[-50%] translate-y-[-50%] pointer-events-none transition-transform duration-1000 ease-in-out w-full h-full p-2"
      >
        <component :is="circleComponent" :color="selectedColor" />
      </div>

      <FlyingStarsComponent
        v-if="showStars"
        :azimuthFacade="circleAngle"
        :stars="flyingStars"
        :color="selectedColor"
      />

      <!-- Стрелка фасада -->
      <div
        ref="arrowRef"
        class="absolute top-1/2 left-1/2 z-[1000] translate-x-[-50%] translate-y-[-50%] cursor-grab select-none h-1/2"
        style="transform-origin: center center"
        @mousedown="startRotation"
        @touchstart="startRotation"
      >
        <FacadeArrowImage :color="selectedColor" />
      </div>
    </div>
  </div>
</template>

<style scoped>
.input-shadow {
  box-shadow: 0 4px 14px 0 #00000026;
}
</style>
